/**
 * @file Timing, retention, and user-facing copy constants for the upload manager.
 */

/** How long completed upload sessions stay in the Uploads panel. */
export const UPLOAD_SESSION_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/** Stable Uppy instance id — GoldenRetriever localStorage / IndexedDB keys derive from this. */
export const UPPY_INSTANCE_ID = 'surface-upload-manager';

/** Base wait for GoldenRetriever async blob restore before resuming without blobs. */
export const UPLOAD_RECOVERY_TIMEOUT_MS = 10_000;

/** Additional recovery wait per in-flight file (capped by max). */
export const UPLOAD_RECOVERY_TIMEOUT_PER_FILE_MS = 2_000;

/** Maximum GoldenRetriever restore wait regardless of file count. */
export const UPLOAD_RECOVERY_TIMEOUT_MAX_MS = 60_000;

/**
 * Scales GoldenRetriever blob-restore wait for multi-file sessions.
 * @param fileCount - Number of in-flight files awaiting blob restore.
 */
export const getUploadRecoveryTimeoutMs = (fileCount: number): number =>
  Math.min(
    UPLOAD_RECOVERY_TIMEOUT_MAX_MS,
    UPLOAD_RECOVERY_TIMEOUT_MS +
      Math.max(0, fileCount) * UPLOAD_RECOVERY_TIMEOUT_PER_FILE_MS
  );

/** How long a queued/uploading file may sit without progress before reporting stalled. */
export const UPLOAD_STALL_TIMEOUT_MS = 90_000;

/** Toast title shown when the first file in a new session begins uploading. */
export const UPLOAD_STARTED_TOAST_TITLE = 'Upload started';

/** Toast body for the upload-started notification. */
export const UPLOAD_STARTED_TOAST_DESCRIPTION =
  'You can continue using the application while we upload your file in the background.';

/** Minimum estimated upload duration before showing the upload-started toast. */
export const UPLOAD_STARTED_TOAST_MIN_ESTIMATED_DURATION_MS = 10_000;

/** Conservative upload speed used to estimate whether an enqueue is "instant". */
export const UPLOAD_STARTED_TOAST_ASSUMED_BYTES_PER_SECOND = 512 * 1024;

/** Delay before retrying a failed upload session completion handler. */
export const UPLOAD_SESSION_COMPLETION_RETRY_DELAY_MS = 5_000;
